// Between spans of code (spanNote.ts) and the diff as drawn: a cell is a
// `.source-cell[data-side][data-line]`, whose `<code>` holds exactly the
// line's source text (render.ts only wraps pieces of it), so a column is a
// count of that text's characters.
import type { Side, SpanPoint } from './spanNote';

/** A line's cell and its code, from a node in it. */
function cellOf(node: Node): { cell: HTMLElement; code: HTMLElement; side: Side; line: number } | null {
  const el = node.nodeType === 1 ? (node as Element) : node.parentElement;
  const cell = el?.closest<HTMLElement>('.source-cell[data-side][data-line]');
  const code = cell?.querySelector<HTMLElement>(':scope > code');
  if (!cell || !code) return null;
  const side = cell.dataset.side;
  const line = Number(cell.dataset.line);
  if ((side !== 'LEFT' && side !== 'RIGHT') || !Number.isInteger(line) || line < 1) return null;
  return { cell, code, side, line };
}

/** How many of `code`'s characters come before DOM position (`node`, `offset`), which is inside it. */
function colIn(code: HTMLElement, node: Node, offset: number): number {
  const r = (code.ownerDocument ?? document).createRange();
  r.setStart(code, 0);
  r.setEnd(node, offset);
  return r.toString().length;
}

/**
 * The line and column of DOM position (`node`, `offset`) in the diff, on
 * its side: inside a line's code, the characters before it; elsewhere in
 * the line's cell (its number, its sign), the line's start, or past the
 * code (a button after it), its end. Null outside every line's cell.
 */
export function pointAt(node: Node, offset: number): (SpanPoint & { side: Side }) | null {
  const c = cellOf(node);
  if (!c) return null;
  if (c.code.contains(node)) return { side: c.side, line: c.line, col: colIn(c.code, node, offset) };
  const before = !!(c.code.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING);
  return { side: c.side, line: c.line, col: before ? 0 : (c.code.textContent ?? '').length };
}

/**
 * The DOM position `col` characters into `code`'s text: between two text
 * nodes, a range's start goes at the next one's start (`start`), its end at
 * the last one's end, so the range holds only its own text nodes.
 */
function domPoint(code: HTMLElement, col: number, start: boolean): { node: Node; offset: number } {
  const walker = (code.ownerDocument ?? document).createTreeWalker(code, 4 /* NodeFilter.SHOW_TEXT */);
  let left = col;
  let last: Text | null = null;
  for (let t = walker.nextNode() as Text | null; t; t = walker.nextNode() as Text | null) {
    if (start ? left < t.data.length : left <= t.data.length) return { node: t, offset: left };
    left -= t.data.length;
    last = t;
  }
  return last ? { node: last, offset: last.data.length } : { node: code, offset: code.childNodes.length };
}

/**
 * The span `start`–`end` on `side` as DOM ranges, one per line drawn under
 * `root` (a split view puts the other side's cells between its lines, so
 * one range would take those in too); lines not drawn (folded) have none.
 */
export function rangesFor(root: ParentNode, side: Side, start: SpanPoint, end: SpanPoint): Range[] {
  const out: Range[] = [];
  for (let n = start.line; n <= end.line; n++) {
    const code = root.querySelector<HTMLElement>(`.source-cell[data-side="${side}"][data-line="${n}"] > code`);
    if (!code) continue;
    const len = (code.textContent ?? '').length;
    const from = n === start.line ? Math.min(start.col, len) : 0;
    const to = n === end.line ? Math.min(end.col, len) : len;
    if (to <= from) continue;
    const a = domPoint(code, from, true), b = domPoint(code, to, false);
    const r = (code.ownerDocument ?? document).createRange();
    r.setStart(a.node, a.offset);
    r.setEnd(b.node, b.offset);
    out.push(r);
  }
  return out;
}
